/**
 * TenantSuspendedPaywall - Componente de paywall para tenants suspensos
 * 
 * Exibe layout diferenciado baseado no motivo da suspensão:
 * - trial_expired: Mostra os 3 planos pagos para contratação
 * - multiple_free_tenants: Mostra opções de trocar empresa ou fazer upgrade
 */

import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Building2, 
  Crown,
  Check,
  Sparkles,
  Shield,
  Headphones,
  RotateCcw,
  AlertTriangle,
  ArrowRight,
  RefreshCw,
  Clock,
  Zap,
  Inbox,
  Users,
  MessageSquare,
  Package,
  CreditCard,
  ExternalLink
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
// v1.2.0 (2026-07-22) — Segrega planos exibidos entre estrutura Legada e
// Planos 2.0 (integracao_v1), reutilizando o mesmo hook que o PlansRouter usa
// para decidir qual UI de /tenant/plans renderizar.
import { useTenantPlansViewMode } from '@/hooks/tenant/useTenantPlansV2';

// Tipo do motivo da suspensão.
// v1.1.0 (2026-07-02) — Adicionado 'awaiting_first_payment' para bloquear
// tenants Planos 2.0 pré-pagos que ainda não pagaram a 1ª fatura.
type SuspensionReason =
  | 'multiple_free_tenants'
  | 'trial_expired'
  | 'payment_failed'
  | 'awaiting_first_payment'
  | null;

interface TenantSuspendedPaywallProps {
  tenantName: string;
  suspensionReason?: SuspensionReason;
  onSwitchTenant?: () => void;
}

/**
 * Card de Plano Individual para layout de trial expirado
 */
const PlanCard = ({
  plan,
  isPopular,
  isEnterprise,
  onSelect
}: {
  plan: any;
  isPopular: boolean;
  isEnterprise: boolean;
  onSelect: () => void;
}) => {
  const features = typeof plan.features === 'string' 
    ? JSON.parse(plan.features) 
    : (Array.isArray(plan.features) ? plan.features : []);

  const formatPrice = (value: number | null) => {
    if (!value || value === 0) return '0';
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div
      className={cn(
        "relative flex flex-col rounded-2xl border transition-all duration-300 cursor-pointer",
        // Enterprise - Card escuro premium
        isEnterprise && "bg-slate-900 dark:bg-slate-950 border-slate-700 text-white",
        // Popular (Professional) - Destaque máximo
        isPopular && !isEnterprise && "border-purple-500/50 shadow-xl shadow-purple-500/10",
        // Cards normais
        !isPopular && !isEnterprise && "bg-card hover:border-primary/30",
        // Hover effect
        "hover:translate-y-[-4px] hover:shadow-xl"
      )}
      onClick={onSelect}
    >
      {/* Badge "Mais Popular" */}
      {isPopular && !isEnterprise && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
          <Badge className="bg-purple-600 hover:bg-purple-600 text-white border-0 shadow-lg px-4 py-1">
            <Sparkles className="h-3 w-3 mr-1.5" />
            Mais Popular
          </Badge>
        </div>
      )}

      <div className="p-6 flex-1 flex flex-col">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className={cn(
              "flex h-10 w-10 items-center justify-center rounded-xl",
              isEnterprise 
                ? "bg-amber-500/20 text-amber-400"
                : isPopular 
                  ? "bg-purple-500/10 text-purple-600 dark:text-purple-400"
                  : "bg-blue-500/10 text-blue-600 dark:text-blue-400"
            )}>
              {isEnterprise ? <Crown className="h-5 w-5" /> :
               isPopular ? <Sparkles className="h-5 w-5" /> :
               <Zap className="h-5 w-5" />}
            </div>
            <h3 className={cn(
              "text-xl font-semibold tracking-tight",
              isEnterprise && "text-white"
            )}>
              {plan.name}
            </h3>
          </div>
          <p className={cn(
            "text-sm leading-relaxed",
            isEnterprise ? "text-slate-400" : "text-muted-foreground"
          )}>
            {plan.description || 'Ideal para equipes em crescimento'}
          </p>
        </div>

        {/* Preço */}
        <div className="mb-6">
          <div className="flex items-baseline gap-1">
            <span className={cn(
              "text-sm font-medium",
              isEnterprise ? "text-slate-400" : "text-muted-foreground"
            )}>
              R$
            </span>
            <span className={cn(
              "text-3xl font-bold tracking-tight",
              isEnterprise && "text-white"
            )}>
              {formatPrice(plan.price_monthly)}
            </span>
            <span className={cn(
              "text-sm font-medium",
              isEnterprise ? "text-slate-400" : "text-muted-foreground"
            )}>
              /mês
            </span>
          </div>
        </div>

        <Separator className={cn("mb-4", isEnterprise && "bg-slate-700")} />

        {/* Limites do plano */}
        <div className="space-y-3 mb-4">
          <div className="flex items-center gap-3 text-sm">
            <Inbox className={cn(
              "h-4 w-4 flex-shrink-0",
              isEnterprise ? "text-amber-400" : isPopular ? "text-purple-500" : "text-primary"
            )} />
            <span className={isEnterprise ? "text-slate-300" : ""}>
              {plan.max_inboxes === -1 ? 'Inboxes ilimitadas' : `${plan.max_inboxes} inbox${plan.max_inboxes > 1 ? 'es' : ''}`}
            </span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Users className={cn(
              "h-4 w-4 flex-shrink-0",
              isEnterprise ? "text-amber-400" : isPopular ? "text-purple-500" : "text-primary"
            )} />
            <span className={isEnterprise ? "text-slate-300" : ""}>
              {plan.max_agents === -1 ? 'Agentes ilimitados' : `${plan.max_agents} agente${plan.max_agents > 1 ? 's' : ''}`}
            </span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <MessageSquare className={cn(
              "h-4 w-4 flex-shrink-0",
              isEnterprise ? "text-amber-400" : isPopular ? "text-purple-500" : "text-primary"
            )} />
            <span className={isEnterprise ? "text-slate-300" : ""}>
              {plan.max_messages_month === null || plan.max_messages_month === -1 
                ? 'Mensagens ilimitadas' 
                : `${(plan.max_messages_month / 1000).toFixed(0)}k msgs/mês`}
            </span>
          </div>
        </div>

        {/* Features extras */}
        {features.length > 0 && (
          <div className="flex-1 space-y-2 mb-6">
            {features.slice(0, 4).map((feature: string, idx: number) => (
              <div key={idx} className="flex items-start gap-2 text-sm">
                <Check className={cn(
                  "h-4 w-4 mt-0.5 flex-shrink-0",
                  isEnterprise 
                    ? "text-amber-400" 
                    : isPopular 
                      ? "text-purple-500" 
                      : "text-emerald-500"
                )} />
                <span className={isEnterprise ? "text-slate-300" : "text-muted-foreground"}>
                  {feature}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Botão de Ação */}
        <Button
          variant={isPopular || isEnterprise ? "default" : "outline"}
          size="lg"
          className={cn(
            "w-full mt-auto",
            isEnterprise && "bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold",
            isPopular && !isEnterprise && "bg-purple-600 hover:bg-purple-700 font-semibold"
          )}
        >
          Escolher Plano
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

/**
 * Layout para Trial Expirado - Mostra os 3 planos pagos
 */
const TrialExpiredLayout = ({ 
  tenantName, 
  plans, 
  isLoading, 
  onSelectPlan 
}: { 
  tenantName: string; 
  plans: any[]; 
  isLoading: boolean;
  onSelectPlan: () => void;
}) => {
  // Filtra apenas planos pagos (exclui 'free')
  const paidPlans = plans.filter(p => p.id !== 'free' && p.is_active);

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-6">
      <div className="container max-w-5xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          {/* Ícone de relógio com estilo premium */}
          <div className="relative inline-flex items-center justify-center w-20 h-20 mb-6">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-amber-500/20 via-orange-500/20 to-red-500/20 blur-xl animate-pulse" />
            <div className="relative flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 shadow-lg shadow-orange-500/25">
              <Clock className="h-10 w-10 text-white" />
            </div>
          </div>
          
          <h1 className="text-3xl font-semibold tracking-tight mb-4">
            Período de Avaliação Expirado
          </h1>
          
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-4">
            O período de avaliação gratuita de <span className="font-medium text-foreground">{tenantName}</span> terminou.
            <br />Escolha um plano para continuar usando todos os recursos.
          </p>
          
          {/* Badge informativo */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50 text-sm text-muted-foreground">
            <Shield className="h-4 w-4" />
            Seus dados estão preservados e aguardando sua volta
          </div>
        </div>

        {/* Grid de Planos */}
        <div className="mb-12">
          <h2 className="text-xl font-semibold text-center mb-8">
            Escolha um plano para continuar
          </h2>
          
          {isLoading ? (
            <div className="grid md:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-[450px] rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              {paidPlans.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  isPopular={plan.id === 'professional'}
                  isEnterprise={plan.id === 'enterprise'}
                  onSelect={onSelectPlan}
                />
              ))}
            </div>
          )}
        </div>

        {/* Informação adicional */}
        <div className="text-center p-6 rounded-xl bg-muted/50 border mb-8">
          <Building2 className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <h3 className="font-medium mb-2">Seus dados estão seguros</h3>
          <p className="text-sm text-muted-foreground max-w-lg mx-auto">
            Todas as suas conversas, contatos e configurações foram preservados. 
            Ao escolher um plano, você terá acesso imediato a todos os seus dados.
          </p>
        </div>

        {/* Footer de Confiança */}
        <div className="flex flex-wrap items-center justify-center gap-8 py-6 border-t">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Shield className="h-4 w-4" />
            <span>Pagamento seguro via Stripe</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Headphones className="h-4 w-4" />
            <span>Suporte dedicado</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <RotateCcw className="h-4 w-4" />
            <span>7 dias de garantia</span>
          </div>
        </div>

        <p className="text-center text-sm text-muted-foreground mt-4">
          Dúvidas? Entre em contato com nosso suporte para ajudá-lo a escolher a melhor opção.
        </p>
      </div>
    </div>
  );
};

/**
 * Layout para Pagamento Pendente - Mostra opções de atualizar método de pagamento
 */
const PaymentFailedLayout = ({ 
  tenantName,
  isLoading,
  navigate,
  handleOpenBillingPortal,
  variant = 'payment_failed',
}: { 
  tenantName: string;
  isLoading: boolean;
  navigate: (path: string) => void;
  handleOpenBillingPortal: () => void;
  /**
   * v1.1.0 — Diferencia a copy entre "cobrança falhou (recorrente)" e
   * "aguardando o 1º pagamento da nova contratação".
   */
  variant?: 'payment_failed' | 'awaiting_first_payment';
}) => {
  // Copy dinâmica conforme o motivo do bloqueio
  const isAwaitingFirst = variant === 'awaiting_first_payment';
  const heroTitle = isAwaitingFirst
    ? 'Aguardando confirmação de pagamento'
    : 'Pagamento Pendente';
  const heroDescription = isAwaitingFirst
    ? (<>Sua contratação de <span className="font-medium text-foreground">{tenantName}</span> foi registrada, mas ainda estamos aguardando a confirmação do primeiro pagamento.<br />Conclua o pagamento para liberar o acesso ao sistema.</>)
    : (<>Não conseguimos processar o pagamento de <span className="font-medium text-foreground">{tenantName}</span>.<br />Atualize seu método de pagamento para continuar usando o sistema.</>);
  const badgeText = isAwaitingFirst
    ? 'Acesso liberado após o pagamento'
    : 'Acesso limitado até a regularização';
  const cardTitle = isAwaitingFirst
    ? 'Conclua o Pagamento'
    : 'Atualize seu Pagamento';
  const cardDescription = isAwaitingFirst
    ? 'Acesse a página de Planos para visualizar a fatura em aberto e finalizar o pagamento. Assim que confirmado pelo provedor, seu acesso é liberado automaticamente.'
    : 'Acesse o portal de faturamento para atualizar seu método de pagamento ou verificar o status da sua assinatura.';
  const primaryCtaLabel = isAwaitingFirst ? 'Ir para Planos e Pagar' : 'Atualizar Pagamento';
  const primaryCtaAction = isAwaitingFirst
    ? () => navigate('/tenant/plans')
    : handleOpenBillingPortal;

  // =========================================================================
  // v1.2.0 (2026-07-02) — Layout PREMIUM dedicado para awaiting_first_payment.
  // Mantém a variante 'payment_failed' original intocada logo abaixo.
  // Baseado no protótipo "Moderno sleek" aprovado pelo usuário.
  // =========================================================================
  if (isAwaitingFirst) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-muted/30 p-6">
        <div className="w-full max-w-xl flex flex-col items-center animate-fade-in">
          {/* ============ Hero: ícone + títulos ============ */}
          <div className="text-center mb-8">
            <div className="relative inline-flex items-center justify-center mb-6">
              {/* Glow suave pulsante por trás do ícone hero */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-orange-400/30 to-red-500/30 blur-2xl animate-pulse" />
              <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-400 to-red-500 shadow-lg shadow-orange-500/25">
                <CreditCard className="w-8 h-8 text-white" strokeWidth={1.75} />
              </div>
            </div>

            <h1 className="text-3xl font-bold text-foreground tracking-tight mb-3">
              {heroTitle}
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-md mx-auto">
              {heroDescription}
            </p>
          </div>

          {/* ============ Badge de status (tom calmo, não alarmista) ============ */}
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rose-50 dark:bg-rose-950/40 border border-rose-100 dark:border-rose-900/60 text-rose-600 dark:text-rose-400 text-sm font-medium">
              <AlertTriangle className="w-4 h-4" />
              {badgeText}
            </div>
          </div>

          {/* ============ Card principal de ação ============ */}
          <div className="w-full bg-card rounded-3xl border border-border shadow-xl shadow-slate-200/40 dark:shadow-slate-950/40 p-8 mb-6">
            <div className="flex flex-col items-center text-center mb-8">
              <div className="w-12 h-12 rounded-full bg-rose-50 dark:bg-rose-950/40 flex items-center justify-center mb-4">
                <CreditCard className="w-6 h-6 text-rose-500" />
              </div>
              <h2 className="text-xl font-bold text-foreground mb-2">{cardTitle}</h2>
              <p className="text-muted-foreground text-sm max-w-sm">
                Assim que confirmado pelo provedor, seu acesso é liberado automaticamente.
              </p>
            </div>

            {/* Bullets — sem promessa de meios de pagamento específicos */}
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-3 text-muted-foreground">
                <Check className="w-5 h-5 text-emerald-500 shrink-0" strokeWidth={2.5} />
                <span className="text-sm">Visualize a fatura em aberto</span>
              </div>
              <div className="flex items-center gap-3 text-muted-foreground">
                <Check className="w-5 h-5 text-emerald-500 shrink-0" strokeWidth={2.5} />
                <span className="text-sm">Pague pelos meios liberados no seu plano</span>
              </div>
              <div className="flex items-center gap-3 text-muted-foreground">
                <Check className="w-5 h-5 text-emerald-500 shrink-0" strokeWidth={2.5} />
                <span className="text-sm">Liberação imediata após confirmação do pagamento</span>
              </div>
            </div>

            {/* CTA principal — fundo escuro, seta desliza no hover */}
            <button
              type="button"
              onClick={primaryCtaAction}
              className="w-full bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-white dark:text-slate-900 text-white font-semibold py-4 px-6 rounded-2xl flex items-center justify-center gap-2 transition-all group"
            >
              {primaryCtaLabel}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>

          {/* ============ Bloco de confiança — "Seus dados estão seguros" ============ */}
          <div className="w-full bg-muted/50 border border-border/60 rounded-2xl p-6 mb-6">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-card rounded-lg shadow-sm border border-border shrink-0">
                <Shield className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-1">
                  Seus dados estão seguros
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Todas as suas conversas, contatos e configurações estão preservados.
                  Ao regularizar o pagamento, seu acesso será restaurado imediatamente.
                </p>
              </div>
            </div>
          </div>

          {/* ============ Footer: link secundário + selos de confiança ============ */}
          <div className="w-full text-center space-y-8">
            <button
              type="button"
              onClick={() => navigate('/tenant/plans')}
              className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Ou veja outros planos disponíveis
              <ArrowRight className="w-4 h-4" />
            </button>

            <div className="flex flex-col items-center gap-4 border-t border-border pt-8">
              <div className="flex flex-wrap items-center justify-center gap-6 text-muted-foreground text-xs font-medium uppercase tracking-widest">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Pagamento seguro via Stripe
                </div>
                <div className="flex items-center gap-2">
                  <Headphones className="w-4 h-4" />
                  Suporte dedicado
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Problemas com pagamento? Entre em contato com nosso suporte.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fallback: variante 'payment_failed' (cobrança recorrente falhou) — layout legado
  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-6">
      <div className="container max-w-2xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          {/* Ícone de cartão com estilo de alerta */}
          <div className="relative inline-flex items-center justify-center w-20 h-20 mb-6">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-red-500/20 via-orange-500/20 to-amber-500/20 blur-xl animate-pulse" />
            <div className="relative flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-red-500 via-orange-500 to-amber-500 shadow-lg shadow-red-500/25">
              <CreditCard className="h-10 w-10 text-white" />
            </div>
          </div>
          
          <h1 className="text-3xl font-semibold tracking-tight mb-4">
            {heroTitle}
          </h1>
          
          <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-4">
            {heroDescription}
          </p>
          
          {/* Badge informativo */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-destructive/10 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4" />
            {badgeText}
          </div>
        </div>

        {/* Card Principal de Ação */}
        <Card className="relative overflow-hidden border-destructive/20 hover:shadow-lg transition-all duration-200 mb-8">
          <CardContent className="pt-8 pb-6 px-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
                <CreditCard className="h-8 w-8 text-destructive" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{cardTitle}</h3>
              <p className="text-muted-foreground">
                {cardDescription}
              </p>
            </div>
            
            <Separator className="my-6" />
            
            {/* Opções (adaptadas por variante) */}
            <div className="space-y-3 mb-6">
              {isAwaitingFirst ? (
                <>
                  <div className="flex items-center gap-3 text-sm">
                    <Check className="h-4 w-4 text-emerald-500" />
                    <span className="text-muted-foreground">Visualize a fatura em aberto</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Check className="h-4 w-4 text-emerald-500" />
                    <span className="text-muted-foreground">Pague pelos meios liberados no seu plano</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Check className="h-4 w-4 text-emerald-500" />
                    <span className="text-muted-foreground">Acesso liberado assim que o pagamento for confirmado</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-3 text-sm">
                    <Check className="h-4 w-4 text-emerald-500" />
                    <span className="text-muted-foreground">Atualize cartão de crédito</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Check className="h-4 w-4 text-emerald-500" />
                    <span className="text-muted-foreground">Visualize faturas pendentes</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Check className="h-4 w-4 text-emerald-500" />
                    <span className="text-muted-foreground">Acesso restaurado imediatamente</span>
                  </div>
                </>
              )}
            </div>
            
            <Button 
              className="w-full"
              size="lg"
              onClick={primaryCtaAction}
              disabled={isLoading && !isAwaitingFirst}
            >
              <CreditCard className="mr-2 h-4 w-4" />
              {primaryCtaLabel}
              <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>

        {/* Informação adicional */}
        <div className="text-center p-6 rounded-xl bg-muted/50 border mb-8">
          <Shield className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <h3 className="font-medium mb-2">Seus dados estão seguros</h3>
          <p className="text-sm text-muted-foreground max-w-lg mx-auto">
            Todas as suas conversas, contatos e configurações estão preservados.
            Ao regularizar o pagamento, seu acesso será restaurado imediatamente.
          </p>
        </div>

        {/* Link alternativo */}
        <div className="text-center">
          <Button 
            variant="ghost" 
            className="text-sm text-muted-foreground"
            onClick={() => navigate('/tenant/plans')}
          >
            Ou veja outros planos disponíveis
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>

        {/* Footer de Confiança */}
        <div className="flex flex-wrap items-center justify-center gap-8 py-6 border-t mt-8">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Shield className="h-4 w-4" />
            <span>Pagamento seguro via Stripe</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Headphones className="h-4 w-4" />
            <span>Suporte dedicado</span>
          </div>
        </div>

        <p className="text-center text-sm text-muted-foreground mt-4">
          Problemas com pagamento? Entre em contato com nosso suporte.
        </p>
      </div>
    </div>
  );
};

/**
 * Layout para Múltiplos Tenants Free - Mostra opções de trocar ou fazer upgrade
 */
const MultipleFreeTenantsLayout = ({ 
  tenantName, 
  onSwitchTenant,
  campaignPlan,
  isLoading,
  formatPrice,
  navigate
}: { 
  tenantName: string; 
  onSwitchTenant?: () => void;
  campaignPlan: any;
  isLoading: boolean;
  formatPrice: (value: number | null) => string;
  navigate: (path: string) => void;
}) => {
  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-6">
      <div className="container max-w-4xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          {/* Ícone de alerta com estilo premium */}
          <div className="relative inline-flex items-center justify-center w-20 h-20 mb-6">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-amber-500/20 via-orange-500/20 to-red-500/20 blur-xl animate-pulse" />
            <div className="relative flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 shadow-lg shadow-orange-500/25">
              <AlertTriangle className="h-10 w-10 text-white" />
            </div>
          </div>
          
          <h1 className="text-3xl font-semibold tracking-tight mb-4">
            Empresa Suspensa
          </h1>
          
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-4">
            <span className="font-medium text-foreground">{tenantName}</span> está suspensa porque
            você já possui outra empresa gratuita ativa.
          </p>
          
          {/* Explicação da regra */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50 text-sm text-muted-foreground">
            <Shield className="h-4 w-4" />
            O plano Free permite apenas 1 empresa ativa por usuário
          </div>
        </div>

        {/* Cards de Ação */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {/* Card Trocar de Empresa - Neutro */}
          <Card className="relative overflow-hidden hover:shadow-lg hover:translate-y-[-2px] transition-all duration-200">
            <CardContent className="pt-8 pb-6 px-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                  <RefreshCw className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-semibold">Trocar de Empresa</h3>
                  <p className="text-sm text-muted-foreground">
                    Acesse sua empresa gratuita ativa
                  </p>
                </div>
              </div>
              
              {/* Descrição */}
              <div className="py-6 border-y border-border/50 my-4">
                <p className="text-sm text-muted-foreground text-center">
                  Use o seletor de empresas no topo da tela para acessar sua empresa ativa
                  ou selecione outra empresa que você tenha acesso.
                </p>
              </div>
              
              {/* Benefícios */}
              <div className="space-y-2 mb-6">
                <div className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Acesso imediato</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Sem custo adicional</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Dados preservados</span>
                </div>
              </div>
              
              {onSwitchTenant && (
                <Button 
                  variant="outline"
                  className="w-full hover:scale-[1.02] active:scale-[0.98] transition-transform"
                  onClick={onSwitchTenant}
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Selecionar Outra Empresa
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Card Upgrade - Premium Dark */}
          <Card className="relative overflow-hidden bg-slate-900 text-white border-0 hover:shadow-2xl hover:shadow-purple-500/10 hover:translate-y-[-2px] transition-all duration-200">
            {/* Gradient border effect */}
            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-purple-500/20 via-pink-500/20 to-purple-500/20 opacity-50" />
            
            <div className="absolute top-4 right-4 z-10">
              <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 font-normal">
                <Sparkles className="h-3 w-3 mr-1" />
                Recomendado
              </Badge>
            </div>
            
            <CardContent className="relative pt-8 pb-6 px-6 z-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                  <Crown className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Fazer Upgrade</h3>
                  <p className="text-sm text-slate-400">
                    Desbloqueie múltiplas empresas
                  </p>
                </div>
              </div>
              
              {/* Preço */}
              <div className="py-6 text-center border-y border-slate-700/50 my-4">
                <p className="text-sm text-slate-400 mb-1">
                  Planos a partir de
                </p>
                {isLoading ? (
                  <Skeleton className="h-12 w-32 mx-auto bg-slate-700" />
                ) : (
                  <p className="text-4xl font-bold text-white">
                    R$ {formatPrice(campaignPlan?.price_monthly)}
                    <span className="text-base font-normal text-slate-400">/mês</span>
                  </p>
                )}
              </div>
              
              {/* Benefícios premium */}
              <div className="space-y-2 mb-6">
                <div className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-amber-400" />
                  <span className="text-slate-300">Múltiplas empresas ativas</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-amber-400" />
                  <span className="text-slate-300">Mais inboxes e agentes</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-amber-400" />
                  <span className="text-slate-300">Recursos avançados</span>
                </div>
              </div>
              
              <Button 
                className="w-full bg-white text-slate-900 hover:bg-slate-100 hover:scale-[1.02] active:scale-[0.98] transition-all"
                onClick={() => navigate('/tenant/plans')}
                disabled={isLoading}
              >
                <Crown className="mr-2 h-4 w-4" />
                Ver Planos Disponíveis
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Informação adicional */}
        <div className="text-center p-6 rounded-xl bg-muted/50 border mb-8">
          <Building2 className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <h3 className="font-medium mb-2">O que acontece com esta empresa?</h3>
          <p className="text-sm text-muted-foreground max-w-lg mx-auto">
            Seus dados estão seguros e preservados. Ao fazer upgrade, esta empresa será 
            reativada automaticamente com todos os dados intactos.
          </p>
        </div>

        {/* Footer de Confiança */}
        <div className="flex flex-wrap items-center justify-center gap-8 py-6 border-t">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Shield className="h-4 w-4" />
            <span>Pagamento seguro via Stripe</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Headphones className="h-4 w-4" />
            <span>Suporte dedicado</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <RotateCcw className="h-4 w-4" />
            <span>7 dias de garantia</span>
          </div>
        </div>

        <p className="text-center text-sm text-muted-foreground mt-4">
          Dúvidas? Entre em contato com nosso suporte para ajudá-lo a escolher a melhor opção.
        </p>
      </div>
    </div>
  );
};

export function TenantSuspendedPaywall({ 
  tenantName, 
  suspensionReason,
  onSwitchTenant 
}: TenantSuspendedPaywallProps) {
  const navigate = useNavigate();
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);

  // v1.2.0 — Descobre a versão vigente do tenant (Legado vs Planos 2.0)
  // antes de buscar os planos, para não misturar as duas estruturas na UI.
  const { data: viewMode, isLoading: isLoadingViewMode } = useTenantPlansViewMode();

  // Busca planos filtrados pela versão vigente do tenant.
  // - integracao_v1 → apenas planos da estrutura 2.0
  // - legado       → apenas planos legados (mesmo filtro usado em useTenantBilling)
  const { data: plans = [], isLoading: isLoadingPlansQuery } = useQuery({
    queryKey: ['subscription-plans', viewMode ?? 'pending'],
    enabled: !!viewMode,
    queryFn: async () => {
      let query = supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (viewMode === 'integracao_v1') {
        query = query.eq('billing_integration', 'integracao_v1');
      } else {
        // 'legado' — espelha o filtro de src/hooks/useTenantBilling.ts
        query = query.neq('billing_integration', 'integracao_v1');
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    }
  });

  // Loading combinado: mantém o skeleton enquanto qualquer etapa não terminou.
  const isLoadingPlans = isLoadingViewMode || isLoadingPlansQuery;

  // Plano mais barato para exibir no layout multiple_free_tenants
  const campaignPlan = plans.find(p => p.id === 'starter') || plans.find(p => p.id !== 'free');

  // Formatação de preço
  const formatPrice = (value: number | null) => {
    if (!value || value === 0) return '0';
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Handler para redirecionar para página de planos
  const handleSelectPlan = () => {
    navigate('/tenant/plans');
  };

  // Handler para abrir portal Stripe (atualizar pagamento)
  const handleOpenBillingPortal = async () => {
    setIsOpeningPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-portal', {
        body: { returnUrl: window.location.origin + '/tenant/plans' }
      });

      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Erro ao abrir portal de faturamento:', error);
      // Fallback para página de planos
      navigate('/tenant/plans');
    } finally {
      setIsOpeningPortal(false);
    }
  };

  // Renderiza layout baseado no motivo da suspensão.
  // v1.1.0 — 'awaiting_first_payment' reutiliza o layout de pagamento pendente
  // com copy e CTA voltados para a 1ª contratação (aponta para /tenant/plans).
  if (
    suspensionReason === 'payment_failed' ||
    suspensionReason === 'awaiting_first_payment'
  ) {
    return (
      <PaymentFailedLayout
        tenantName={tenantName}
        isLoading={isOpeningPortal}
        navigate={navigate}
        handleOpenBillingPortal={handleOpenBillingPortal}
        variant={suspensionReason}
      />
    );
  }

  if (suspensionReason === 'trial_expired') {
    return (
      <TrialExpiredLayout
        tenantName={tenantName}
        plans={plans}
        isLoading={isLoadingPlans}
        onSelectPlan={handleSelectPlan}
      />
    );
  }

  // Layout padrão para multiple_free_tenants ou quando não há motivo especificado
  return (
    <MultipleFreeTenantsLayout
      tenantName={tenantName}
      onSwitchTenant={onSwitchTenant}
      campaignPlan={campaignPlan}
      isLoading={isLoadingPlans}
      formatPrice={formatPrice}
      navigate={navigate}
    />
  );
}
